import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    startWith,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { isEqual } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';

import { DropdownDirection as DroppanelOpenDirection } from '../../../form-controls/entities/dropdown-direction.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ClearMarginModifiers } from '../../entities/clear-margin-modifier.entity';
import { ArrowNavigationHelper } from '../../helpers/arrow-navigation.helper';
import { ActionItem, ActionItemModifier, ActionItemStyle } from '../action-item/entities/action-item.entity';
import { DroppanelStyle } from '../droppanel/entities/droppanel-style.entity';
import { IconSize } from '../icon/entities/icon-sizes.entity';

@Directive()
export abstract class BaseDroppanelSelectComponent<T = string>
    extends BaseContainer
    implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
    protected readonly OPTION_QUERY: string = '.sh-droppanel__list-action:not(.-sh-disabled)';
    protected readonly DELETE_INPUT_KEYS: string[] = ['Backspace', 'Delete', 'Clear'];

    /* Base sh-droppanel properties */
    public limitHeightToViewport$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /* Selection properties */
    protected selectedState$!: Observable<FormValue<FormSelectOption<T>>[]>;
    protected initialSelectedValues$: ReplaySubject<FormValue<T>[]> = new ReplaySubject<FormValue<T>[]>();
    readonly state$: BehaviorSubject<FormValue<FormSelectOption<T>>[]> = new BehaviorSubject<
        FormValue<FormSelectOption<T>>[]
    >([]);

    public selectCount: number = 0;

    public selectedValues$!: Observable<T[]>;
    public showExtraItemsLabelIndicator$!: Observable<boolean>;

    /* Search properties */
    protected searchTerm$!: Observable<string>;
    public searchControl: UntypedFormControl;

    /* Options */
    protected readonly multiFormSelectOptions$: ReplaySubject<MultiFormSelectOptions<T>> = new ReplaySubject<
        MultiFormSelectOptions<T>
    >(1);

    public hasOptions$!: Observable<boolean>;

    /* Delete */
    readonly deletePress$: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();

    /* Others */
    public sizes: typeof IconSize = IconSize;
    public states: typeof FormValueState = FormValueState;
    public clearMarginModifiers: ClearMarginModifiers = ClearMarginModifiers.CLEAR_MARGIN_TOP_IMPORTANT;

    // Injectors
    protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
    protected arrowNavigation: ArrowNavigationHelper;

    @HostListener('window:scroll', ['$event'])
    onWindowScroll(event: Event) {
        // A fixed panel is re-pointed at its toggle on every scroll by `sh-droppanel`, so it has somewhere to
        // be and closing it only takes the menu away mid-gesture.
        if (this.inOverflownContainer) {
            return;
        }

        this.setDropdownClosed();
    }

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        this.arrowNavigation.handleKeyDown({
            event,
            optionContainerElement: this.optionContainerElement,
            toggleContainer: this.toggleContainer,
            searchElement: this.searchElement,
            closeDropdown: () => this.setDropdownClosed()
        });
    }

    /* Messages */
    @Input() noResultsFoundMsg: string = this.translateService.translate('ui.no-results');
    @Input() loadingMsg = this.translateService.translate('ui.loading');
    @Input() searchPlaceholderMsg: string = this.translateService.translate('ui.search');
    @Input() selectionLimitMessageKey: string = 'ui.selection-limit';

    /* Base sh-droppanel inputs */
    @Input() inOverflownContainer = false;
    @Input() height = 300;
    @Input() openDirection: DroppanelOpenDirection = DroppanelOpenDirection.Below;
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;

    @Input() isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Input() loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);

    @Input() set limitHeightToViewport(limitHeightToViewport: boolean) {
        this.limitHeightToViewport$.next(limitHeightToViewport);
    }

    /* Specific sh-droppanel-select inputs */
    @Input() id?: string = 'droppanel';
    @Input() search = false;
    @Input() multiple = false;
    @Input() allowSingleDeselect = true;
    @Input() sortAlphabetically = false;
    @Input() valueToLabelFct?: (value: T) => string;
    @Input() selectionLimit: number = Number.MAX_SAFE_INTEGER;
    @Input() hiddenItemCount: number = 0; // Items that contribute to the selectCap, but are not present in the component

    @Input() set initialSelectedValues(values: FormValue<T>[]) {
        if (values) {
            this.initialSelectedValues$.next(values);
        }
    }

    @ViewChild('toggleContainer') toggleContainer: ElementRef<HTMLElement>;
    @ViewChild('searchInput') searchElement?: ElementRef<HTMLElement>;
    @ViewChild('optionContainer') optionContainerElement?: ElementRef<HTMLElement>;

    constructor(protected readonly formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.initArrowNavigation();
        this.initControls();
        this.initSourceStreams();
        this.initStateStreams();
        this.initListeners();
        this.initHasOptionsStream();
        this.onInit();
    }

    ngAfterViewInit(): void {
        this.initStateChangeListener();
    }

    ngAfterViewChecked() {
        this.arrowNavigation.manageFocus(this.isOpen$.value, this.optionContainerElement, this.searchElement);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.limitHeightToViewport$.complete();
        this.multiFormSelectOptions$.complete();
        this.state$.complete();
        this.deletePress$.complete();
        this.onDestroy();
    }

    // #region Template methods for subclasses
    protected onInit(): void {}
    protected onDestroy(): void {}
    // #endregion

    // #region Abstract methods
    protected abstract initStateChangeListener(): void;
    protected abstract initHasOptionsStream(): void;
    protected abstract getLoadMoreLabel(): string;
    protected abstract onLoadMore(): void;
    // #endregion

    protected initArrowNavigation(): void {
        this.arrowNavigation = new ArrowNavigationHelper(
            this.cdr,
            this.isOpen$,
            this.destroy$,
            this.OPTION_QUERY,
            true
        );
    }

    protected initControls(): void {
        this.searchControl = this.formBuilder.control('');
    }

    // #region initSourceStreams
    protected initSourceStreams(): void {
        this.searchTerm$ = this.buildSearchTermStream();
        this.selectedState$ = this.buildSelectedStateStream();
    }

    protected buildSearchTermStream(): Observable<string> {
        if (this.search) {
            return (this.searchControl.valueChanges as Observable<string>).pipe(
                debounceTime(300),
                distinctUntilChanged(isEqual),
                tap((searchTerm: string) => this.onSearchTermChange(searchTerm)),
                startWith('')
            );
        } else {
            return of('');
        }
    }

    protected onSearchTermChange(searchTerm: string): void {
        // Override in subclass if needed
    }

    protected buildSelectedStateStream(): Observable<FormValue<FormSelectOption<T>>[]> {
        return this.state$.pipe(
            map(formValues => formValues.filter(formValue => formValue.state !== FormValueState.UNSET))
        );
    }
    // #endregion

    protected initStateStreams(): void {
        this.selectedValues$ = this.state$.pipe(
            map(input => input.filter(formValue => formValue.state === FormValueState.SET)),
            map(input => input.map(formValue => formValue.value.value))
        );
    }

    protected initListeners(): void {
        this.initInputListener();
        this.initDeletePressListener();
        this.initOptionsListener();
        this.initOpenListener();
    }

    // #region initListeners
    protected initInputListener(): void {
        this.initialSelectedValues$
            .pipe(
                withLatestFrom(this.multiFormSelectOptions$.pipe(startWith({ isLiveData: true, options: [] }))),
                takeUntil(this.destroy$)
            )
            .subscribe(([formValues, multiFormSelectOptions]) => {
                this.setState(this.setOptionSelected(formValues, multiFormSelectOptions));
                // Count the amount of set formValues
                this.selectCount = formValues.filter(formValue => formValue.state === this.states.SET).length;
            });

        // Once the options are loaded, check if any labels needs updating
        this.multiFormSelectOptions$
            .pipe(takeUntil(this.destroy$))
            .subscribe(options => this.updateStateWithOptions(options.options));
    }

    protected updateStateWithOptions(options: FormSelectOption<T>[]): void {
        this.state$.pipe(take(1), takeUntil(this.destroy$)).subscribe(state => {
            const newState: FormValue<FormSelectOption<T>>[] = state.map(formValue => {
                if (formValue.value.value !== '') {
                    return formValue;
                }

                const formSelectOption: FormSelectOption<T> | undefined = options.find(option =>
                    isEqual(option.value, formValue.value.value)
                );
                if (formSelectOption) {
                    return new FormValue(formSelectOption, formValue.state);
                }

                return formValue;
            });

            this.setState(newState);
        });
    }

    protected setOptionSelected(
        formValues: FormValue<T>[],
        multiFormSelectOptions: MultiFormSelectOptions<T>
    ): FormValue<FormSelectOption<T>>[] {
        // Base selection, based on the available values in multiFormSelectOptions
        const options: FormValue<FormSelectOption<T>>[] = multiFormSelectOptions.options.map(formSelectOption => {
            const value: T = formSelectOption.value;
            const formValue: FormValue<T> | undefined = formValues.find(fv => isEqual(fv.value, value));
            const state: FormValueState = formValue ? formValue.state : FormValueState.UNSET;
            return new FormValue(formSelectOption, state);
        });

        /* When multiFormSelectOptions are live, the contents do not represent all selectable values,
        but can be dependent on e.g. search query, pagination,...
            Allow selection of any random value. It is up to the wrapper code to make sure it's a valid value.
         */
        if (multiFormSelectOptions.isLiveData) {
            const extraSelected: FormValue<T>[] = formValues.filter(formvalue =>
                options.every(option => !isEqual(option.value.value, formvalue.value))
            );

            const extraState: FormValue<FormSelectOption<T>>[] = extraSelected.map(
                formValue =>
                    new FormValue(
                        { label: this.estimateLabelOfMissingValue(formValue.value), value: formValue.value },
                        formValue.state
                    )
            );
            options.push(...extraState);
        }

        return options;
    }

    // Estimate label for values that are not present in provided options
    // Functions can be manually provided via valueToLabelFct to translate objects
    protected estimateLabelOfMissingValue(value: T): string {
        if (typeof value === 'string') {
            return value;
        } else if (!!this.valueToLabelFct) {
            return this.valueToLabelFct(value);
        }

        return '';
    }

    protected initDeletePressListener(): void {
        this.deletePress$
            .pipe(
                withLatestFrom(this.state$),
                map(([_, state]) => state),
                filter(state => state.length > 0),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.unsetAllOptions();
            });
    }

    protected initOpenListener(): void {
        this.isOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
            if (isOpen) {
                this.arrowNavigation.resetFocus();
                this.activate();
            } else if (this.isActiveComponent()) {
                this.deactivate();
            }
        });
    }

    protected initOptionsListener(): void {
        this.multiFormSelectOptions$
            .pipe(distinctUntilChanged(isEqual), withLatestFrom(this.state$), takeUntil(this.destroy$))
            .subscribe(([options, state]) => {
                const newState: FormValue<FormSelectOption<T>>[] = state;
                let modified = false;

                options.options.forEach((option: FormSelectOption<T>) => {
                    const index: number = newState.findIndex((state: FormValue<FormSelectOption<T>>) =>
                        isEqual(state.value.value, option.value)
                    );

                    if (index > -1) {
                        newState[index] = new FormValue(option, newState[index].state);
                        modified = true;
                    }
                });

                if (modified) {
                    this.setState(newState);
                }
            });
    }
    // #endregion

    // #region Search
    protected resetSearch(): void {
        if (this.search) {
            this.searchControl.reset('');
        }
    }
    // #endregion

    // #region State
    protected setState(newState: FormValue<FormSelectOption<T>>[]): void {
        this.state$.next(newState);
    }

    public getState(formSelectOption: FormSelectOption<T>): FormValueState {
        const formValue: FormValue<FormSelectOption<T>> | undefined = this.state$.value.find(fv =>
            isEqual(fv.value.value, formSelectOption.value)
        );

        if (typeof formValue === 'undefined') {
            return FormValueState.UNSET;
        }
        return formValue.state;
    }

    public isIndeterminate(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.INDETERMINATE;
    }

    public isUnset(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.UNSET;
    }

    public isSet(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.SET;
    }
    // #endregion

    // #region Option helpers
    protected unsetAllOptions(): void {
        const newState: FormValue<FormSelectOption<T>>[] = this.state$.value.map(
            formValue => new FormValue(formValue.value, FormValueState.UNSET)
        );
        this.setState(newState);
    }

    public toggleCheckbox(event: Event, selectedOption: FormSelectOption<T>): void {
        let target: HTMLElement | null = event.target as HTMLElement | null;
        if (target && target?.tagName !== 'INPUT') {
            target = target.querySelector('input');
            (target as HTMLInputElement).checked = !(target as HTMLInputElement).checked;
        }
        const isChecked: boolean = (target as HTMLInputElement).checked;

        if (isChecked && selectedOption.disabled) {
            return;
        }

        const state: FormValueState = isChecked ? FormValueState.SET : FormValueState.UNSET;

        const currentValue: FormValue<FormSelectOption<T>>[] = [...this.state$.value];
        const index: number = currentValue.findIndex(fv => isEqual(fv.value.value, selectedOption.value));

        if (index > -1) {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(currentValue[index].value, state);
            currentValue.splice(index, 1, formValue);
        } else {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(selectedOption, FormValueState.SET);
            currentValue.push(formValue);
        }

        if (isChecked) {
            this.selectCount++;
        } else {
            this.selectCount--;
        }

        this.setState(currentValue);
    }

    public selectSingleValue(selectedOption: FormSelectOption<T>): void {
        if (selectedOption.disabled) {
            return;
        }

        this.resetSearch();

        if (this.isSet(selectedOption) && this.allowSingleDeselect) {
            this.unsetAllOptions();
            this.setDropdownClosed();
            return;
        }

        this.setState([new FormValue(selectedOption)]);
        this.setDropdownClosed();
    }
    // #endregion

    // #region Helpers
    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    public handleDeletePress(keyboardEvent: KeyboardEvent): void {
        if (this.DELETE_INPUT_KEYS.includes(keyboardEvent.key)) {
            this.deletePress$.next(keyboardEvent);
        }
    }

    public trackByFn(index: number, option: FormSelectOption<T>): T {
        return option.value;
    }

    public getDisabledConfig(
        option: FormSelectOption,
        state: FormValueState
    ): { isDisabled: boolean; disabledReason: string } {
        const inherentDisabled: boolean = !!option.disabled;
        const notSetAndUnsetDisabled: boolean = !(state === this.states.SET && option.allowUnsetDisabled);
        const selectCapReached: boolean = this.selectCount + this.hiddenItemCount >= this.selectionLimit;
        const selectionCapDisabled: boolean = selectCapReached && state !== FormValueState.SET;

        let disabledReason: string = '';

        if (inherentDisabled && notSetAndUnsetDisabled) {
            disabledReason = option.disabledInfoMsg ?? '';
        } else if (selectionCapDisabled) {
            disabledReason = this.translateService.instant(this.selectionLimitMessageKey, {
                count: this.selectionLimit
            });
        }

        return {
            isDisabled: (inherentDisabled && notSetAndUnsetDisabled) || selectionCapDisabled,
            disabledReason
        };
    }

    public get loadMoreActionItem(): ActionItem {
        return {
            label: this.getLoadMoreLabel(),
            style: ActionItemStyle.Link,
            modifiers: [ActionItemModifier.Bold],
            eventListener: () => this.onLoadMore()
        };
    }

    public onSearchFocus(): void {
        this.arrowNavigation.onSearchFocus();
    }

    public get focusIndex(): number {
        return this.arrowNavigation?.focusIndex ?? -1;
    }

    public onHover(event: MouseEvent): void {
        this.arrowNavigation.onHover(event.target as HTMLElement, this.optionContainerElement);
    }
    // #endregion
}
